import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter,
  signal,
  ChangeDetectorRef,
} from "@angular/core";
import {
  ImageCropperComponent,
  ImageCroppedEvent,
} from "ngx-image-cropper";
import { UtilService } from "../../providers/util.service";
import { NgClass } from "@angular/common";
import { LoadingOverlayComponent } from "../loading-overlay/loading-overlay.component";
import { BvIconComponent } from "../../design-system/primitives/icon.component";
import { MemberAvatarComponent } from "../member-avatar/member-avatar.component";
import { FileInputComponent } from "../file-input/file-input.component";
import { BvButtonComponent } from "../../design-system/primitives/button.component";

@Component({
    selector: "app-photo-picker",
    templateUrl: "./photo-picker.component.html",
    styleUrls: ["./photo-picker.component.css"],
    imports: [LoadingOverlayComponent, NgClass, BvIconComponent, MemberAvatarComponent, FileInputComponent, BvButtonComponent, ImageCropperComponent]
})
export class PhotoPickerComponent implements OnInit, OnDestroy {
  @Input() prevImg: any;
  @Input() editable: any;
  @Input() showDelete: boolean = false;
  @Input() roundCropper: boolean = true;
  @Input() aspectRatio: number = 1;
  @Input() resizeToWidth: number = 384;
  @Input() fallbackUID!: string;
  @Input() fallbackName!: string;
  @Input() fallbackSize: number = 160;
  // When set, the empty-state placeholder is this large bv-icon instead of the
  // member avatar — e.g. a landscape icon for the winner-photo dialog where
  // there's no meaningful avatar to preview.
  @Input() placeholderIcon!: string;
  @Output() onCrop = new EventEmitter<any>();
  @Output() onDelete = new EventEmitter<void>();
  cropperOpen: boolean = false;
  webcamOpen: boolean = false;
  isDesktop: boolean = false;
  // Signal so the async enumerateDevices() write below is CD-coordinated under
  // zoneless — a plain field mutated in the promise callback changed mid-CD and
  // tripped NG0100 on the `@if (availableCameras().length > 1)` camera-switch button.
  readonly availableCameras = signal<MediaDeviceInfo[]>([]);
  imgBase64: any;
  newImg: string | null | undefined;
  cropperReady: boolean = false;
  // Whole-canvas rotation in integer 90° steps (ngx-image-cropper v8 replaced
  // the imperative rotateLeft() method with the canvasRotation input).
  canvasRotation: number = 0;
  @ViewChild(ImageCropperComponent, { static: false })
  imageCropper!: ImageCropperComponent;
  @ViewChild("videoRef", { static: false })
  videoRef!: ElementRef<HTMLVideoElement>;
  private stream!: MediaStream | null;

  constructor(
    public utilServ: UtilService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.isDesktop =
      typeof window !== "undefined" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  }

  ngOnDestroy() {
    this.stopStream();
  }

  get hasPrev(): boolean {
    return !!this.prevImg && this.utilServ.hasPlayerImage(this.prevImg);
  }

  openWebcam() {
    this.webcamOpen = true;
    this.startStream();
  }

  switchCamera() {
    const cameras = this.availableCameras();
    if (cameras.length <= 1) return;
    const currentId = this.stream
      ?.getVideoTracks()[0]
      ?.getSettings().deviceId;
    const currentIndex = cameras.findIndex(
      (d) => d.deviceId === currentId
    );
    const nextIndex = (currentIndex + 1) % cameras.length;
    this.startStream(cameras[nextIndex].deviceId);
  }

  private startStream(deviceId?: string) {
    this.stopStream();
    const videoConstraints: MediaTrackConstraints = {
      width: { ideal: 1280 },
      height: { ideal: 720 },
    };
    if (deviceId) {
      videoConstraints.deviceId = { exact: deviceId };
    }
    navigator.mediaDevices
      .getUserMedia({ video: videoConstraints })
      .then((stream) => {
        this.stream = stream;
        setTimeout(() => {
          if (this.videoRef) {
            this.videoRef.nativeElement.srcObject = stream;
          }
        });
        navigator.mediaDevices.enumerateDevices().then((devices) => {
          this.availableCameras.set(
            devices.filter((d) => d.kind === "videoinput")
          );
        });
      })
      .catch((err) => {
        console.error("getUserMedia failed", err);
        this.webcamOpen = false;
        this.cdr.markForCheck(); // getUserMedia rejection — notify CD under zoneless
      });
  }

  snap() {
    const video = this.videoRef.nativeElement;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    this.imgBase64 = canvas.toDataURL("image/jpeg", 0.92);
    this.closeWebcam();
    this.cropperOpen = true;
  }

  closeWebcam() {
    this.webcamOpen = false;
    this.stopStream();
  }

  private stopStream() {
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
  }

  imageChange(file: File) {
    this.normalizeOrientation(file)
      .then((base64) => {
        this.imgBase64 = base64;
        this.cropperOpen = true;
        this.cdr.markForCheck(); // img.onload promise — notify CD under zoneless
      })
      .catch(() => {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.imgBase64 = e.target.result;
          this.cropperOpen = true;
          this.cdr.markForCheck(); // FileReader onload — notify CD under zoneless
        };
        reader.readAsDataURL(file);
      });
  }

  // Browsers auto-rotate <img> per EXIF, but ngx-image-cropper also applies
  // EXIF rotation manually — double-rotating the image. Bake the rotation in
  // via canvas and strip EXIF so the cropper sees an already-upright image.
  private normalizeOrientation(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          URL.revokeObjectURL(url);
          reject(new Error("no canvas context"));
          return;
        }
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/jpeg", 0.92));
      };
      img.onerror = (err) => {
        URL.revokeObjectURL(url);
        reject(err);
      };
      img.src = url;
    });
  }

  imageCropped(event: ImageCroppedEvent) {
    this.newImg = event.base64;
    this.onCrop.emit(event);
  }
  imageLoaded() {
    this.cropperReady = true;
  }
  loadImageFailed() {
    console.log("Image load failed");
  }
  crop() {
    const evt = this.imageCropper.crop("base64");
    if (evt) this.imageCropped(evt);
    this.cropperOpen = false;
    this.cropperReady = false;
  }
  reset() {
    this.cropperOpen = false;
    this.imgBase64 = null;
    this.newImg = null;
    this.cropperReady = false;
    this.canvasRotation = 0;
  }
  rotateLeft() {
    this.canvasRotation--;
  }
}
